import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { timeAgo, useLoad } from "../lib/hooks";
import { applyTheme, getTheme, type Theme } from "../lib/theme";
import { canListen, canSpeak, getVoicePrefs, setVoicePrefs, speak, type VoicePrefs } from "../lib/voice";
import type { Me as MeT } from "../lib/types";

const COLORS = ["#E3A21A", "#3D7A4C", "#2F6FA3", "#B8432F", "#7A4FA0", "#2E8C88", "#C26A1E", "#5B6B2F"];
interface Invite { code: string; createdAt: number; usedAt: number | null; usedBy: { username: string; displayName: string } | null }

export function Me() {
  const { user, setUser, logout } = useAuth();
  const [name, setName] = useState(user!.displayName);
  const [msg, setMsg] = useState("");
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [theme, setTheme] = useState<Theme>(getTheme());
  const [voice, setVoice] = useState<VoicePrefs>(getVoicePrefs());
  const setV = (v: VoicePrefs) => { setVoice(v); setVoicePrefs(v); };
  const invites = useLoad(() => api<{ invites: Invite[] }>("/invites"));
  const [fresh, setFresh] = useState<{ code: string; url: string } | null>(null);

  const save = async (body: object, ok: string) => {
    setMsg("");
    try { const r = await api<{ user: MeT }>("/me", { method: "PATCH", body }); setUser(r.user); setMsg(ok); }
    catch (e: any) { setMsg(e.message); }
  };
  const invite = async () => {
    try { const r = await api<{ code: string; url: string }>("/invites", { method: "POST" }); setFresh(r); invites.reload(); }
    catch (e: any) { setMsg(e.message); }
  };
  const share = async () => {
    if (!fresh) return;
    const text = `Join me on Mathtrain: ${fresh.url}`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* dismissed */ } }
    else { await navigator.clipboard?.writeText(fresh.url); setMsg("Link copied."); }
  };

  return <main className="page">
    <h1>{user!.displayName}</h1>
    <p className="mute small">@{user!.username}{user!.isAdmin ? ", admin" : ""}</p>

    {user!.canInvite && <><h2>Invite friends</h2>
      <div className="card">
        <p className="mute small" style={{ marginTop: 0 }}>Each code works once. Send the link and they land on the join page with the code filled in.</p>
        {fresh && <div className="card" style={{ background: "var(--c-bg)", marginBottom: 10 }}><div className="big-num">{fresh.code}</div>
          <button className="btn ink" style={{ marginTop: 8, width: "100%" }} onClick={share}>Share invite link</button></div>}
        <button className="btn-primary" onClick={invite}>Create invite code</button>
        {invites.data && invites.data.invites.length > 0 && <ul className="board" style={{ marginTop: 10 }}>
          {invites.data.invites.map((i) => <li key={i.code}><div className="who"><b>{i.code}</b><span>{i.usedBy ? `used by ${i.usedBy.displayName}` : `unused, made ${timeAgo(i.createdAt)}`}</span></div></li>)}
        </ul>}
      </div></>}

    <h2>Profile</h2>
    <div className="card">
      <label className="field"><span>Display name</span><input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} /></label>
      <div className="small mute" style={{ marginBottom: 6 }}>Color</div>
      <div className="swatches">{COLORS.map((c) => <button key={c} aria-label={`Color ${c}`} aria-pressed={user!.color === c} style={{ background: c }} onClick={() => save({ color: c }, "Color saved.")} />)}</div>
      <button className="btn ink" style={{ marginTop: 14, width: "100%" }} disabled={!name.trim() || name === user!.displayName} onClick={() => save({ displayName: name }, "Name saved.")}>Save name</button>
    </div>

    <h2>Appearance</h2>
    <div className="seg">{(["system", "light", "dark"] as Theme[]).map((t) => <button key={t} aria-pressed={theme === t} onClick={() => { setTheme(t); applyTheme(t); }}>{t === "system" ? "Match system" : t[0].toUpperCase() + t.slice(1)}</button>)}</div>

    {(canSpeak() || canListen()) && <>
      <h2>Voice</h2>
      <div className="card">
        {canSpeak() && <label className="check"><input type="checkbox" checked={voice.speak} onChange={(e) => setV({ ...voice, speak: e.target.checked })} />
          <span>Read problems aloud<small className="small mute"> Uses your device's own voice. Nothing leaves your device.</small></span></label>}
        {canListen() && <label className="check"><input type="checkbox" checked={voice.listen} onChange={(e) => setV({ ...voice, listen: e.target.checked })} />
          <span>Answer by speaking<small className="small mute"> Your browser's speech service turns your voice into text. On Chrome that is Google, on Safari it is Apple, so your audio may be sent to them. Mathtrain itself never receives or stores audio.</small></span></label>}
        <p className="small mute" style={{ margin: "8px 0" }}>Voice sessions get a little extra time per problem to cover hearing it and saying the answer. Say “repeat”, “hint”, “skip”, or “no” to cancel an answer before it's sent.</p>
        {canSpeak() && <button className="btn ink" style={{ width: "100%" }} onClick={() => speak("Forty seven times six. Two hundred eighty two.")}>Test the voice</button>}
      </div>
    </>}

    <h2>Password</h2>
    <div className="card">
      <label className="field"><span>Current password</span><input type="password" autoComplete="current-password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} /></label>
      <label className="field"><span>New password</span><input type="password" autoComplete="new-password" minLength={8} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} /></label>
      <button className="btn ink" style={{ width: "100%" }} disabled={!pw.currentPassword || pw.newPassword.length < 8} onClick={() => { save(pw, "Password changed."); setPw({ currentPassword: "", newPassword: "" }); }}>Change password</button>
    </div>
    <div className="error" role="status" style={{ color: msg.endsWith(".") && !msg.includes("incorrect") ? "var(--c-good)" : undefined }}>{msg}</div>
    <button className="btn danger" onClick={logout}>Sign out</button>
  </main>;
}
